<template>
  <div>
    <div class="details-stack">
      <v-row>
        <v-col cols="12">
          <AppSelectField
            :model-value="package_id"
            :items="packages"
            item-title="label"
            item-value="id"
            label="Membership Package"
            label-icon="mdi-crown-outline"
            placeholder="Select package"
            :loading="isLoadingPackages"
            :disabled="isLoadingPackages"
            :error-messages="errors.package_id ? [errors.package_id] : []"
            @update:modelValue="emit('update:package_id', String($event || ''))"
          />
        </v-col>
      </v-row>

      <div v-if="selectedPackage" class="plan-card">
        <div class="plan-billing-toggle">
          <div class="billing-toggle-container">
            <div class="billing-header">
            </div>
            <div class="custom-toggle">
              <div class="toggle-track">
                <div class="toggle-option" :class="{ active: billingInterval === 'monthly' }" @click="handleIntervalChange('monthly')">
                  <span class="toggle-text">Monthly</span>
                </div>
                <div class="toggle-slider" :class="{ yearly: billingInterval === 'yearly' }"></div>
                <div class="toggle-option" :class="{ active: billingInterval === 'yearly' }" @click="handleIntervalChange('yearly')">
                  <span class="toggle-text">Yearly</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="plan-header">
          <div class="plan-title">
            <span class="plan-name">{{ selectedPackage.label }}</span>
          </div>
        </div>
        <div class="plan-pricing">
          <div class="price-with-period">
            <span v-if="selectedPackagePriceText" class="plan-price">
              {{ selectedPackagePriceText }}
            </span>
            <span class="billing-period">/{{ billingInterval }}</span>
          </div>
          <div v-if="billingInterval === 'yearly'" class="monthly-equivalent">
            <span>{{ monthlyEquivalent }}</span>
            <span v-if="yearlySavings" class="savings-text">
              <v-icon size="10" color="success">mdi-tag</v-icon>
              Save {{ yearlySavings }}
            </span>
          </div>
        </div>

        <div class="plan-highlight">{{ selectedPackage.highlight }}</div>

        <div class="plan-features">
          <div
            v-for="(feature, idx) in selectedPackage.features"
            :key="idx"
            class="feature-row"
          >
            <v-icon size="18" color="success">mdi-check</v-icon>
            <span>{{ feature }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import AppSelectField from "@/shared/components/ui/AppSelectField.vue";
import type { MembershipPackage } from "@/shared/services/vendor-requests.api";
import { MEMBERSHIP_PACKAGES_PRESETS } from "@/modules/vendor/membership-packages.presets";
import { formatCurrency } from "@/shared/utils/utils";
import {getCompanyPlan } from "@/shared/services/vendor-requests.api";
import { useRouter, useRoute } from "vue-router";

const props = withDefaults(
  defineProps<{
    package_id: string;
    billing_interval: "monthly" | "yearly";
    errors: {
      package_id?: string;
    };
  }>(),
  {
    package_id: "",
    billing_interval: "monthly",
    errors: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "update:package_id", value: string): void;
  (e: "update:billing_interval", value: "monthly" | "yearly"): void;
  (e: "packages-loaded", value: MembershipPackage[]): void;
}>();

const packages = ref<MembershipPackage[]>([]);
const isLoadingPackages = ref(false);
const billingInterval = ref<"monthly" | "yearly">(props.billing_interval);

onMounted(() => {
  loadPackages();
});

const normalizePackages = (payload: any): MembershipPackage[] => {
  const raw =
    Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : Array.isArray(payload?.packages) ? payload.packages : [];

  if (!Array.isArray(raw)) return [];

  return raw
    .filter(Boolean)
    .map((p: any) => ({
      ...p,
      id: String(p?.id ?? ""),
    }))
    .filter((p: any) => p.id);
};
const router = useRouter();
const route = useRoute();
// Resolve company id from route params (:companyId) — always expected to be present.
function resolveCompanyId(): number | null {
  // Prefer route params (e.g. /company/:companyId/apply-membership)
  let raw: any = route.params.companyId ?? router.currentRoute.value.params?.companyId ?? null;
  if (Array.isArray(raw)) raw = raw[0] ?? null;
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string" && /^\d+$/.test(raw)) return Number(raw);
  // Attempt numeric coercion as a last resort
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}


const loadPackages = async () => {
  isLoadingPackages.value = true;
  try {

    let data;
    // if (isVendorMode()) {
    //   const rawCompanyId = router.currentRoute.value.query.company_id;
    //   let companyId: string | number = "";

    //   if (Array.isArray(rawCompanyId)) {
    //     companyId = rawCompanyId[0] ?? "";
    //   } else {
    //     companyId = rawCompanyId ?? "";
    //   }

    //   // convert numeric string to number so the argument matches string | number
    //   if (typeof companyId === "string" && /^\d+$/.test(companyId)) {
    //     companyId = Number(companyId);
    //   }

      data = await getCompanyPlan(resolveCompanyId() ?? undefined);
    // }else{
    //   data = await getMembershipPackageList();
    // }
    const list = normalizePackages(data);
    const hasRequiredPrices = (p: any) =>
      p &&
      (typeof p.price_monthly === "string" || typeof p.price_monthly === "number") &&
      (typeof p.price_yearly === "string" || typeof p.price_yearly === "number");
    const next = list.length && list.every(hasRequiredPrices) ? list : MEMBERSHIP_PACKAGES_PRESETS;
    packages.value = next;
    emit("packages-loaded", next);
  } catch (err) {
    packages.value = MEMBERSHIP_PACKAGES_PRESETS;
    emit("packages-loaded", MEMBERSHIP_PACKAGES_PRESETS);
    // eslint-disable-next-line no-console
    console.error("Failed to load membership packages", err);
  } finally {
    isLoadingPackages.value = false;
  }
};

const handleIntervalChange = (value: string) => {
  const next = value === "yearly" ? "yearly" : "monthly";
  billingInterval.value = next;
  emit("update:billing_interval", next);
};

const selectedPackage = computed(() => {
  const id = String(props.package_id ?? "").trim();
  if (!id) return null;
  return packages.value.find((p) => String(p.id) === id) ?? null;
});

const parsePriceNumber = (value: string | number | undefined) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const parsed = parseFloat(value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const selectedPackagePriceRaw = computed(() => {
  const pkg = selectedPackage.value;
  if (!pkg) return undefined;

  if (billingInterval.value === "yearly") {
    return pkg.price_yearly;
  }

  return pkg.price_monthly;
});

const selectedPackagePriceText = computed(() => {
  const raw = selectedPackagePriceRaw.value;
  if (raw === undefined || raw === null) return "";

  const numeric = parsePriceNumber(raw);
  if (numeric === null) return String(raw);
  return formatCurrency(numeric);
});

const yearlySavings = computed(() => {
  const pkg = selectedPackage.value;
  if (!pkg || !pkg.price_monthly || !pkg.price_yearly) {
    return null;
  }

  const monthlyPrice = parsePriceNumber(pkg.price_monthly);
  const yearlyPrice = parsePriceNumber(pkg.price_yearly);
  if (monthlyPrice === null || yearlyPrice === null) return null;

  const yearlyMonthlyTotal = monthlyPrice * 12;
  const savings = yearlyMonthlyTotal - yearlyPrice;
  const savingsPercentage = Math.round((savings / yearlyMonthlyTotal) * 100);
  
  return savingsPercentage > 0 ? `${savingsPercentage}%` : null;
});

const monthlyEquivalent = computed(() => {
  const pkg = selectedPackage.value;
  if (!pkg || !pkg.price_yearly) {
    return null;
  }

  const yearlyPrice = parsePriceNumber(pkg.price_yearly);
  if (yearlyPrice === null) return null;
  
  const monthlyEq = yearlyPrice / 12;
  return `≈ ${formatCurrency(monthlyEq)}/month`;
});

</script>

<style scoped>
.details-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.plan-card {
  border-radius: 14px;
  border: 1px solid var(--border-color);
  background: #fff;
  padding: 14px;
}

.plan-billing-toggle {
  margin-bottom: 20px;
}

.billing-toggle-container {
  display: flex;
  flex-direction: column;
}

.billing-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.billing-label {
  color: #374151;
  font-size: 16px;
  font-weight: 600;
}

.savings-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #86efac;
  border-radius: 20px;
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.1);
}

.pricing-savings {
  margin-top: 8px;
  align-self: flex-end;
}

.custom-toggle {
  position: relative;
}

.toggle-track {
  position: relative;
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-track:hover {
  border-color: #cbd5e1;
  background: #f1f5f9;
}

.toggle-option {
  flex: 1;
  padding: 10px 16px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.toggle-option.active {
  color: #ffffff;
}

.toggle-option:not(.active) {
  color: #64748b;
}

.toggle-text {
  font-size: 14px;
  font-weight: 600;
}

.toggle-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
}

.toggle-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background: var(--color-theme);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
  box-shadow: 0 2px 8px rgba(232, 97, 47, 0.3);
}

.toggle-slider.yearly {
  transform: translateX(100%);
  background: var(--color-theme);
  box-shadow: 0 2px 8px rgba(232, 97, 47, 0.3);
}

.plan-header {
  margin-bottom: 8px;
}

.plan-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.plan-name {
  color: #1f2430;
  font-size: 16px;
  font-weight: 700;
}

.plan-pricing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  margin-bottom: 12px;
}

.price-with-period {
  display: flex;
  align-items: baseline;
  gap: 2px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.plan-price {
  color: #16a34a;
  font-size: 18px;
  font-weight: 700;
  white-space: nowrap;
  line-height: 1.2;
}

.billing-period {
  color: #667085;
  font-size: 14px;
  font-weight: 500;
}

.savings-text {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
  margin-left: 6px;
}

.monthly-equivalent {
  color: #667085;
  font-size: 12px;
  font-weight: 500;
  margin-top: 2px;
}

.plan-highlight {
  color: #667085;
  font-size: 14px;
  margin-bottom: 12px;
}

.plan-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #1f2430;
  font-size: 14px;
}
</style>
